<script>
  import { pushNotificationsStore } from '../../stores.js';
  import { pushNotifications } from '../pushNotifications.js';
  import { onMount } from 'svelte';

  let dismissed = false;
  let loading = false;
  let showSuccess = false;

  // Check if user already dismissed the banner in this session
  onMount(() => {
    dismissed = sessionStorage.getItem('notificationBannerDismissed') === 'true';
  });

  function dismiss() {
    dismissed = true;
    sessionStorage.setItem('notificationBannerDismissed', 'true');
  }

  async function enableNotifications() {
    loading = true;

    try {
      const success = await pushNotifications.subscribe();

      if (success) {
        showSuccess = true;
        await pushNotificationsStore.updateStatus(pushNotifications);

        // Auto-dismiss after success
        setTimeout(() => {
          dismiss();
        }, 3000);
      } else {
        loading = false;
      }
    } catch (error) {
      console.error('Subscription error:', error);
      loading = false;
    }
  }

  // Don't show banner if:
  // - Already dismissed
  // - Notifications not supported
  // - Already subscribed
  // - Permission denied
  $: shouldShow = !dismissed &&
                  $pushNotificationsStore.supported &&
                  !$pushNotificationsStore.subscribed &&
                  $pushNotificationsStore.permission !== 'denied';
</script>

{#if shouldShow}
  <div class="banner" class:success={showSuccess}>
    <div class="banner-content">
      {#if showSuccess}
        <div class="success-message">
          <svg class="icon-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
          </svg>
          <span>Notifications activées avec succès !</span>
        </div>
      {:else}
        <div class="banner-left">
          <svg class="icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"></path>
          </svg>
          <div class="banner-text">
            <h3>Activez les notifications</h3>
            <p>Recevez des alertes pour les nouveaux quiz et contenus disponibles</p>
          </div>
        </div>

        <div class="banner-actions">
          <button
            class="btn-enable"
            disabled={loading}
            on:click={enableNotifications}
          >
            {#if loading}
              <span class="spinner"></span>
              Activation...
            {:else}
              Activer
            {/if}
          </button>
          <button class="btn-dismiss" on:click={dismiss}>
            Plus tard
          </button>
        </div>
      {/if}
    </div>
  </div>
{/if}

<style>
  .banner {
    background: linear-gradient(135deg, var(--primary-gradient-start) 0%, var(--primary-gradient-end) 100%);
    border-radius: 12px;
    padding: 1.25rem 1.5rem;
    margin-bottom: 1.5rem;
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.2);
    animation: slideDown 0.4s ease-out;
  }

  .banner.success {
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  }

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .banner-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
  }

  .banner-left {
    display: flex;
    align-items: center;
    gap: 1rem;
    flex: 1;
  }

  .icon,
  .icon-success {
    width: 2rem;
    height: 2rem;
    color: white;
    flex-shrink: 0;
  }

  .banner-text h3 {
    margin: 0;
    color: white;
    font-size: 1rem;
    font-weight: 600;
  }

  .banner-text p {
    margin: 0.25rem 0 0 0;
    color: rgba(255, 255, 255, 0.9);
    font-size: 0.875rem;
  }

  .success-message {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    color: white;
    font-weight: 500;
  }

  .banner-actions {
    display: flex;
    gap: 0.75rem;
    flex-shrink: 0;
  }

  .btn-enable,
  .btn-dismiss {
    padding: 0.625rem 1.25rem;
    border: none;
    border-radius: 6px;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
    white-space: nowrap;
  }

  .btn-enable {
    background: white;
    color: var(--primary-gradient-start);
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .btn-enable:hover:not(:disabled) {
    background: #f9fafb;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }

  .btn-enable:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  .btn-dismiss {
    background: rgba(255, 255, 255, 0.2);
    color: white;
    border: 1px solid rgba(255, 255, 255, 0.3);
  }

  .btn-dismiss:hover {
    background: rgba(255, 255, 255, 0.3);
  }

  .spinner {
    display: inline-block;
    width: 1rem;
    height: 1rem;
    border: 2px solid rgba(31, 41, 55, 0.3);
    border-top-color: var(--primary-gradient-start);
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  /* Mobile responsive */
  @media (max-width: 768px) {
    .banner {
      padding: 1rem;
    }

    .banner-content {
      flex-direction: column;
      align-items: stretch;
    }

    .banner-left {
      gap: 0.75rem;
    }

    .icon,
    .icon-success {
      width: 1.5rem;
      height: 1.5rem;
    }

    .banner-text h3 {
      font-size: 0.9rem;
    }

    .banner-text p {
      font-size: 0.8rem;
    }

    .banner-actions {
      width: 100%;
    }

    .btn-enable,
    .btn-dismiss {
      flex: 1;
      justify-content: center;
    }
  }
</style>
